import React from "react";
import { useSelector } from "react-redux";
import { Alert, Box, Typography } from "@mui/material";
import { formatAbsolute } from "../common/hooks";
import { formatErrorCodeLine } from "../common/ErrorCodeMap";

const EVENT_COLORS = {
  CREATED: "#1976d2",
  PRECHECK_STARTED: "#1976d2",
  PRECHECK_FAILED: "#f57f17",
  NEEDS_INPUTS: "#c62828",
  PERMS_DENIED: "#b71c1c",
  DRY_RUN_READY: "#e65100",
  APPROVED: "#2e7d32",
  IN_PROGRESS: "#1976d2",
  RUNNING: "#1976d2",
  COMPLETED: "#388e3c",
  SUCCEEDED: "#388e3c",
  FAILED: "#c2185b",
  CANCELLED: "#616161",
};

const dotFor = (event) => EVENT_COLORS[event] || "#9e9e9e";

const EVENT_LABEL_ALIASES = {
  requested: "Validation",
  created: "Validation",
  assuming_role: "Validation",
  checking_perms: "Validation",
  precheck_started: "Validation",
  precheck_running: "Validation",
  plan_building: "Validation",
  needs_inputs: "Awaiting Approval",
  dry_run_ready: "Awaiting Approval",
  perms_denied: "Permission Denied",
  approved: "Approved",
  in_progress: "Applying Fix",
  running: "Applying Fix",
  succeeded: "Completed",
  completed: "Completed",
  already_remediated: "Already Remediated",
  failed: "Failed",
  precheck_failed: "Failed",
  verify_failed: "Failed",
  cancelled: "Cancelled",
  abandoned: "Abandoned",
};

const eventKey = (event) => String(event || "").trim().replace(/[\s-]+/g, "_").toLowerCase();

const eventLabel = (event) => {
  const key = eventKey(event);
  if (EVENT_LABEL_ALIASES[key]) return EVENT_LABEL_ALIASES[key];
  const spaced = String(event || "").replace(/_/g, " ");
  return spaced.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

const SYSTEM_ACTOR = /system/i;

/**
 * GET /remediation/requests/{id} `timeline` field: audit events with
 * customer-safe `actor_display` (internal worker IDs masked by the API).
 */
const Timeline = ({ events, requestKind, taskTypeLabel }) => {
  const catalog = useSelector((s) => s.remediation?.errorCodes?.byCode) || {};
  const list = events || [];
  return (
    <Box>
      {taskTypeLabel ? (
        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 1.5 }}>
          Request Type: {taskTypeLabel}
        </Typography>
      ) : null}
      {list.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No history yet.
        </Typography>
      ) : (
    <Box sx={{ position: "relative", pl: 2.5 }}>
      <Box
        sx={{
          position: "absolute",
          left: 6,
          top: 6,
          bottom: 6,
          width: 2,
          backgroundColor: "grey.200",
        }}
      />
      {list.map((ev, idx) => {
        const code = ev.code || ev.error_code;
        const catalogEntry = typeof code === "string" ? catalog[code] : null;
        const catalogLine = formatErrorCodeLine(code, catalogEntry);
        const body = catalogLine || ev.message || ev.note;
        const actionable = catalogEntry
          ? catalogEntry.category === "customer_actionable"
          : Boolean(ev.customer_actionable);
        const key = eventKey(ev.event);
        const hideActor = key === "succeeded" || key === "completed";
        const rawActor = ev.actor_display || "system";
        const actor =
          requestKind === "auto_approved" && key === "approved" && SYSTEM_ACTOR.test(rawActor)
            ? "System (auto-approve)"
            : rawActor;
        return (
          <Box key={idx} sx={{ position: "relative", pb: 2 }}>
            <Box
              sx={{
                position: "absolute",
                left: -16,
                top: 4,
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: dotFor(ev.event),
                border: "2px solid white",
                boxShadow: "0 0 0 2px rgba(0,0,0,0.06)",
              }}
            />
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {eventLabel(ev.event)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {hideActor ? null : `${actor} · `}
                {formatAbsolute(ev.at)}
              </Typography>
            </Box>
            {actionable && body && (
              <Alert severity="warning" sx={{ mt: 1, py: 0.5, fontSize: 13 }}>
                {body}
              </Alert>
            )}
            {!actionable && body && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: "text.primary" }}>
                {body}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
      )}
    </Box>
  );
};

export default Timeline;
